import React, { useContext, useEffect, useState } from 'react';
import './Subscription.css';
import SubscriptionContext from 'context/SubscriptionContext';
import AuthContext from 'context/AuthContext';
import EntitlementContext from 'context/EntitlementContext';
import useCan from 'hooks/useCan';
import { CAPABILITIES } from 'utils/capabilities';
import PurebrandLoader from 'components/Loader/PurebrandLoader';
import { Alert, Button, message } from 'antd';
import {
  FEATURES,
  GENERIC_ERROR_MESSAGE,
  SUBSCRIPTION_MODAL_ACTIONS,
  SUBSCRIPTION_STATUS,
  SUPPORT_REQUEST_TYPES,
} from 'utils/constants';
import SubscriptionModal from 'components/Modal/SubscriptionModal';
import UpgradeSubscriptionModal from 'components/Modal/UpgradeSubscriptionModal';
import SubscriptionManager from 'components/SubscriptionManager/SubscriptionManager';
import { convertToAnyDateFormat } from 'utils/dateFormatter';
import DotsFessAgreement from 'components/Agreements/DotsFessAgreement';

/**
 * This component is used to render the subscription details for the store admin.
 *
 * Every plan card is rendered from the API: the price, the caption, the bullets,
 * the badge and the CTA label are all plan data. Adding a plan, repricing one or
 * rewording a bullet is a database change and needs no edit here.
 *
 * @returns {ReactComponent} A component that renders the subscription details for the store admin.
 */

const Subscription = () => {
  const {
    getSubscriptionData,
    setSubscriptionPlans,
    subscriptionPlans,
    activePlan,
    setActivePlan,
    raiseSupportRequestForStore,
  } = useContext(SubscriptionContext);
  const { userProfile } = useContext(AuthContext);
  const { refreshEntitlements } = useContext(EntitlementContext);
  const { can } = useCan();
  const canManageSubscription = can(CAPABILITIES.SUBSCRIPTION_MANAGE);

  const [currentPlan, setCurrentPlan] = useState({});
  const [manageActivePlan, setManageActivePlan] = useState(false);
  const [operativePlan, setOperativePlan] = useState({});
  const [plansLoading, setPlansLoading] = useState(true);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionModalAction, setSubscriptionModalAction] = useState('');
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeTargetPlan, setUpgradeTargetPlan] = useState({});
  const [supportRequestLoading, setSupportRequestLoading] = useState(false);
  const [supportRequestNotice, setSupportRequestNotice] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  /**
   * This function is used to get all the subscription plans along with the active plan.
   *
   * @returns {void}
   */

  const getSubscriptionInfo = async () => {
    try {
      setPlansLoading(true);
      const res = await getSubscriptionData(
        `?storeId=${userProfile.store.store_id}`
      );

      setSubscriptionPlans(res?.data?.plans || []);
      setActivePlan(res?.data?.active_plan || null);
    } catch (err) {
      console.log(err);
    } finally {
      setPlansLoading(false);
    }
  };

  /**
   * This function returns the rank of the store's active plan, used to decide
   * which plans are upgrades. Ranks come from the plan family, so the comparison
   * keeps working when a tier gains another billing variant.
   *
   * @returns {Number} rank of the active plan, or 0 when there is none
   */

  const getActivePlanRank = () => activePlan?.subscription_type?.tier_rank || 0;

  /**
   * Whether the store is already on a plan.
   *
   * Drives the promotional treatment: the badge and highlight border are there to
   * steer a store that has not chosen yet, so once one is active they would only
   * compete with the card marking the store's own plan.
   *
   * @returns {Boolean} boolean value of true or false
   */

  const hasActiveSubscription = Boolean(activePlan?.subscription_type?.id);

  /**
   * This function is used to check whether the subscription plan is the store's
   * active plan
   *
   * @param {object} plan subscription plan object
   * @returns {Boolean} boolean value of true or false
   */

  const checkActivePlan = (plan) =>
    plan?.id === activePlan?.subscription_type?.id &&
    activePlan?.subscription_status !== SUBSCRIPTION_STATUS.BLOCKED;

  /**
   * This function is used to check whether the subscription plan is paused or not
   *
   * @param {object} plan subscription plan object
   * @returns {Boolean} boolean value of true or false
   */

  const checkPausedPlan = (plan) =>
    plan?.id === activePlan?.subscription_type?.id &&
    activePlan?.subscription_status === SUBSCRIPTION_STATUS.PAUSED;

  /**
   * This function is used to check whether a plan is an upgrade from the active one
   *
   * @param {object} plan subscription plan object
   * @returns {Boolean} boolean value of true or false
   */

  const isUpgrade = (plan) =>
    Boolean(activePlan?.subscription_type?.id) &&
    !plan?.is_custom &&
    plan?.tier_rank > getActivePlanRank();

  /**
   * This function is used to return the text of the plan button.
   *
   * The plan's own CTA label is the default; the states below reflect the
   * store's relationship to the plan, not the plan's identity.
   *
   * @param {object} plan subscription plan object
   * @returns {String} Text to be displayed on the plan button
   */

  const getPlanButtonText = (plan) => {
    if (checkActivePlan(plan)) {
      return checkPausedPlan(plan) ? 'Paused' : 'Your Plan';
    }

    if (isUpgrade(plan)) {
      return 'Upgrade';
    }

    return plan?.cta_label || 'Select Plan';
  };

  /**
   * This function is used to check whether a plan's button can be acted on.
   *
   * Every plan except the store's own is clickable. Lower tiers cannot be
   * switched to directly, but they raise a request with support rather than
   * presenting the store with a dead button and no explanation.
   *
   * @param {object} plan subscription plan object
   * @returns {Boolean} boolean value of true or false
   */

  const isPlanActionable = (plan) =>
    !checkActivePlan(plan) && !supportRequestLoading;

  /**
   * This function is used to handle the plan selection by the store admin.
   *
   * Four outcomes: a custom plan raises an enquiry with sales, a higher tier
   * opens the upgrade flow, a lower tier raises a downgrade request, and a store
   * with no plan yet simply starts one.
   *
   * @param {object} plan subscription plan object
   * @returns {void}
   */

  const handlePlanSelection = (plan) => {
    if (!isPlanActionable(plan)) {
      return;
    }

    // Custom plans are priced by sales, so the CTA opens a conversation rather
    // than sending the store off to a public page.
    if (plan?.is_custom) {
      raiseSupportRequest(SUPPORT_REQUEST_TYPES.ENTERPRISE_ENQUIRY);
      return;
    }

    if (isUpgrade(plan)) {
      setUpgradeTargetPlan(plan);
      setUpgradeModalOpen(true);
      return;
    }

    // A store with no plan yet is choosing one. A store that already has one is
    // asking to move down, which this version does not automate.
    if (activePlan?.subscription_type?.id) {
      raiseSupportRequest(SUPPORT_REQUEST_TYPES.DOWNGRADE, plan);
      return;
    }

    setCurrentPlan(plan);
    setSubscriptionModalOpen(true);
    setSubscriptionModalAction(SUBSCRIPTION_MODAL_ACTIONS.SELECT);
  };

  /**
   * This function is used to manage the active plan for a store
   *
   * @returns {void}
   */

  const handleManagePlan = () => {
    setManageActivePlan(true);
    setOperativePlan(activePlan);
    setTimeout(() => {
      const targetDiv = document.getElementById('sub-manager-outer-container');
      if (targetDiv) {
        targetDiv.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  };

  /**
   * This function is used to manage the cancelling of plan for a store
   *
   * @returns {void}
   */

  const handleCancelPlan = () => {
    setSubscriptionModalOpen(true);
    setCurrentPlan(activePlan?.subscription_type);
    setSubscriptionModalAction(SUBSCRIPTION_MODAL_ACTIONS.CANCEL);
  };

  /**
   * This function is used to manage the pausing or resuming of a store's plan
   *
   * @returns {void}
   */

  const handlePauseOrResumePlan = () => {
    setSubscriptionModalOpen(true);
    setSubscriptionModalAction(SUBSCRIPTION_MODAL_ACTIONS.UPDATE);
    setCurrentPlan(activePlan?.subscription_type);
  };

  /**
   * This function refreshes the plans and the entitlement gates after an upgrade,
   * so the rest of the app unlocks without a page reload.
   *
   * @returns {void}
   */

  const handleUpgradeComplete = async () => {
    await getSubscriptionInfo();
    await refreshEntitlements(userProfile?.store?.store_id);
  };

  /**
   * This function raises a request with the support team for a plan change that
   * cannot be completed self-serve, and reports the outcome to the admin.
   *
   * The confirmation text comes from the API so the message the admin sees
   * always matches what was actually sent, including which address was copied.
   *
   * @param {String} requestType which request to raise
   * @param {object} plan the plan being requested, for a downgrade
   * @returns {void}
   */

  const raiseSupportRequest = async (requestType, plan) => {
    setSupportRequestLoading(true);
    setSupportRequestNotice(null);
    try {
      const res = await raiseSupportRequestForStore({
        request_type: requestType,
        ...(plan?.id ? { subscription_type: plan.id } : {}),
      });

      const content = res?.data?.message || 'Our team has your request.';

      setSupportRequestNotice({ type: 'success', content });
      messageApi.open({ type: 'success', content, duration: 8 });
    } catch (e) {
      const content =
        e?.response?.data?.message || e?.message || GENERIC_ERROR_MESSAGE;

      setSupportRequestNotice({ type: 'error', content });
      messageApi.open({ type: 'error', content, duration: 8 });
    } finally {
      setSupportRequestLoading(false);
    }
  };

  /**
   * This function returns the store's own negotiated terms, resolved from its
   * subscription record rather than the plan row.
   *
   * A custom plan's row is shared by every bespoke customer and carries no real
   * price, so the store's actual figures live in its record's entitlements. For
   * listed plans the two agree, and this is only consulted for the custom one.
   *
   * @returns {object} the resolved monthly fee, percentage and allowances
   */

  const getNegotiatedTerms = () => {
    const entitlements = activePlan?.entitlements || {};
    const usage = activePlan?.usage || {};

    return {
      monthlyFee: entitlements.monthly_fees,
      partnership: entitlements.prosperity_partnership,
      hostLimit: usage.hosts_limit,
      meetingLimit: usage.meetings_limit,
    };
  };

  /**
   * This function formats an allowance for display, where a null limit is the
   * backend's way of saying unlimited rather than zero.
   *
   * @param {Number|null} value the allowance
   * @returns {String} the allowance, or 'unlimited'
   */

  const formatAllowance = (value) =>
    value === null || value === undefined ? 'unlimited' : `${value}`;

  /**
   * This function decides whether a card should show the store's own terms in
   * place of the plan's marketing copy. Only true for the custom plan the store
   * is actually on — everyone else sees the sales pitch.
   *
   * @param {object} plan subscription plan object
   * @returns {Boolean} boolean value of true or false
   */

  const showsNegotiatedTerms = (plan) =>
    checkActivePlan(plan) &&
    plan?.is_custom &&
    getNegotiatedTerms().monthlyFee !== undefined;

  /**
   * This function renders the price of a plan.
   *
   * A custom plan shows its label to anyone shopping, but the store that is on
   * it sees the price it actually pays.
   *
   * @param {object} plan subscription plan object
   * @returns {String} the price to display
   */

  const renderPlanPrice = (plan) => {
    if (showsNegotiatedTerms(plan)) {
      return `$${Number(getNegotiatedTerms().monthlyFee)}`;
    }
    if (plan?.is_custom) {
      return plan?.description || 'Custom';
    }
    return `$${Number(plan?.monthly_fees || 0)}`;
  };

  /**
   * This function renders the sub-price caption. The store on a custom plan sees
   * its own percentage instead of the invitation to get a quote.
   *
   * @param {object} plan subscription plan object
   * @returns {String} the caption to display
   */

  const renderPlanCaption = (plan) => {
    if (showsNegotiatedTerms(plan)) {
      return `per month, plus ${Number(
        getNegotiatedTerms().partnership
      )}% of attributed revenue`;
    }
    return plan?.price_caption || '';
  };

  /**
   * This function renders a plan's feature bullets.
   *
   * For the store's own custom plan the meetings bullet is replaced with its
   * real allowances, since the generic copy ("Beyond 200 meetings a month")
   * describes the offer rather than the deal that was struck. Every other bullet
   * is left as configured.
   *
   * @param {object} plan subscription plan object
   * @returns {Array} the bullets to render
   */

  const getCardFeatures = (plan) => {
    const features = plan?.card_features || [];

    if (!showsNegotiatedTerms(plan)) {
      return features;
    }

    const { hostLimit, meetingLimit } = getNegotiatedTerms();

    return features.map((feature) =>
      feature.code === FEATURES.MEETINGS_LIMIT
        ? {
            ...feature,
            display_text: `Up to ${formatAllowance(
              hostLimit
            )} hosts and ${formatAllowance(meetingLimit)} meetings a month`,
          }
        : feature
    );
  };

  /**
   * This function is used to render the subscription plans for the store admin
   *
   * @returns {ReactComponent} A component that renders the subscription plans for the store admin
   */

  const renderPlans = () => {
    return (
      <>
        {/* Stays on screen until dismissed. The toast confirms the click
            immediately, but it fades, and this is the only record the admin has
            that an email actually went out and where the copy landed. */}
        {supportRequestNotice && (
          <Alert
            className="support-request-alert"
            type={supportRequestNotice.type}
            message={supportRequestNotice.content}
            showIcon
            closable
            onClose={() => setSupportRequestNotice(null)}
          />
        )}

        {activePlan?.subscription_status === SUBSCRIPTION_STATUS.BLOCKED && (
          <div className="paused-subscription-alert">
            <>
              Your subscription is currently blocked. Please reach out to the
              support team
            </>
          </div>
        )}
        {activePlan?.subscription_status === SUBSCRIPTION_STATUS.PAUSED && (
          <div className="paused-subscription-alert">
            <>
              Your subscription is currently paused as of{' '}
              {convertToAnyDateFormat(
                activePlan?.last_paused_at,
                'YYYY-M-DTHH:mmZ',
                'MM/DD/YYYY'
              )[0] || '-'}{' '}
              {canManageSubscription && (
                <>
                  {', '}
                  <button
                    className="resume-subscription-link"
                    onClick={handlePauseOrResumePlan}
                  >
                    click here to resume your subscription.
                  </button>
                </>
              )}
            </>
          </div>
        )}

        {subscriptionPlans.map((plan) => {
          const isActive = checkActivePlan(plan);
          const showBadge = Boolean(plan?.badge_label) && !hasActiveSubscription;

          return (
            <div className="plan-card-wrapper" key={plan?.id}>
              <div
                className={`individual-plan-container ${
                  isActive && 'active-plan-container'
                } ${showBadge && 'highlighted-plan-container'}`}
              >
                {showBadge && (
                  <div className="plan-badge">{plan.badge_label}</div>
                )}
                <div className="plan-content">
                  <div
                    className={`plan-name plan-text-styling ${
                      isActive && 'active-plan-styling active-plan-name'
                    }`}
                  >
                    {plan?.name || '-'}
                  </div>
                  <div
                    className={`plan-monthly-fees plan-text-styling ${
                      isActive && 'active-plan-styling active-plan-monthly-fees'
                    }`}
                  >
                    {renderPlanPrice(plan)}
                  </div>
                  <div
                    className={`plan-duration plan-text-styling ${
                      isActive && 'active-plan-styling active-plan-duration'
                    }`}
                  >
                    {renderPlanCaption(plan)}
                  </div>
                  <div
                    className={`plan-seperator ${
                      isActive && 'active-plan-seperator'
                    }`}
                  ></div>

                  {getCardFeatures(plan).map((feature) => (
                    <div
                      key={feature.code}
                      className={`plan-feature plan-text-styling ${
                        isActive && 'active-plan-styling'
                      }`}
                    >
                      {feature.is_enabled ? (
                        feature.display_text
                      ) : (
                        <s
                          className={`plan-text-styling ${
                            isActive && 'active-plan-styling'
                          }`}
                        >
                          {feature.display_text}
                        </s>
                      )}
                    </div>
                  ))}
                </div>
                {/* Sits outside the content block and is pushed to the card's
                    bottom edge, so the CTA lands on the same line across every
                    card regardless of how many bullets each one carries. */}
                {canManageSubscription ? (
                  <div
                    className={`plan-btn plan-text-styling ${
                      isActive && 'active-plan-styling active-plan-btn'
                    }`}
                  >
                    <Button
                      className={`plan-button ${
                        isActive && 'active-plan-button'
                      } ${checkPausedPlan(plan) && 'paused-plan-button'}`}
                      disabled={!isPlanActionable(plan)}
                      loading={supportRequestLoading && !checkActivePlan(plan)}
                      onClick={() => {
                        handlePlanSelection(plan);
                      }}
                    >
                      {getPlanButtonText(plan)}
                    </Button>
                  </div>
                ) : (
                  isActive && (
                    <div
                      className={`plan-btn plan-text-styling active-plan-styling active-plan-btn`}
                    >
                      <span className="plan-button active-plan-button">
                        {checkPausedPlan(plan) ? 'Paused' : 'Your Plan'}
                      </span>
                    </div>
                  )
                )}
              </div>
              {isActive && canManageSubscription && (
                <Button className="manage-plan-btn" onClick={handleManagePlan}>
                  Manage Plan
                </Button>
              )}
              {/* Reserves the Manage Plan button's footprint on the other cards
                  so every card stays the same height and their bottoms line up.
                  Only needed once some plan actually carries the button. */}
              {!isActive && hasActiveSubscription && canManageSubscription && (
                <div className="manage-plan-placeholder" aria-hidden="true" />
              )}
            </div>
          );
        })}
      </>
    );
  };

  useEffect(() => {
    getSubscriptionInfo();
  }, []);

  if (plansLoading) {
    return <PurebrandLoader />;
  }
  return (
    <>
      {contextHolder}
      <DotsFessAgreement />
      <div className="plans-container">{renderPlans()}</div>
      {manageActivePlan && (
        <div id="sub-manager-outer-container">
          <SubscriptionManager
            activePlan={activePlan}
            handleCloseManager={() => {
              setManageActivePlan(false);
              setOperativePlan({});
            }}
            handleCancelPlan={handleCancelPlan}
            handlePauseOrResumePlan={handlePauseOrResumePlan}
          />
        </div>
      )}

      <SubscriptionModal
        subscriptionModalOpen={subscriptionModalOpen}
        subscriptionModalAction={subscriptionModalAction}
        handleSubscriptionModalClose={() => {
          setSubscriptionModalOpen(false);
          setCurrentPlan({});
        }}
        setManageActivePlan={setManageActivePlan}
        getSubscriptionInfo={getSubscriptionInfo}
        activePlan={activePlan}
        plan={currentPlan}
      />

      <UpgradeSubscriptionModal
        open={upgradeModalOpen}
        plan={upgradeTargetPlan}
        activePlan={activePlan}
        storeId={userProfile?.store?.store_id}
        handleClose={() => {
          setUpgradeModalOpen(false);
          setUpgradeTargetPlan({});
        }}
        onUpgraded={handleUpgradeComplete}
      />
    </>
  );
};

export default Subscription;
